import React, { useEffect } from 'react';
import { trackGiftDownload } from '../utils/supabase';

/**
 * VideoStep Component - Goatzy US Campaign
 * Final page: Assembly video + newsletter confirmation
 */
const VideoStep = ({ onGiftsClaimed, onBack, videoId, productName, manualPdfUrl }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Notify parent when page is viewed
  useEffect(() => {
    if (onGiftsClaimed) {
      onGiftsClaimed();
    }
  }, [onGiftsClaimed]);

  const handleVideoPlay = () => {
    const submissionId = localStorage.getItem('submissionId');
    if (submissionId) {
      trackGiftDownload(submissionId, 'assembly_video');
    }
  };

  // Accept full YouTube URL or just the ID
  const extractYouTubeId = (input) => {
    if (!input) return null;
    const watchMatch = input.match(/[?&]v=([a-zA-Z0-9_-]{10,12})/);
    if (watchMatch) return watchMatch[1];
    const shortMatch = input.match(/youtu\.be\/([a-zA-Z0-9_-]{10,12})/);
    if (shortMatch) return shortMatch[1];
    const embedMatch = input.match(/embed\/([a-zA-Z0-9_-]{10,12})/);
    if (embedMatch) return embedMatch[1];
    return input; // assume it's already an ID
  };
  const youtubeVideoId = extractYouTubeId(videoId) || 'PwDO6Hiqtk4';
  const displayName = productName || 'Goat Stand';

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-goatzy-bg">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="inline-block px-4 py-1 text-xs font-light tracking-wider uppercase bg-goatzy-pale text-goatzy-dark rounded-full mb-4">
            Your Exclusive Content
          </div>
          <h2 className="text-3xl md:text-4xl font-light tracking-wide mb-4 text-goatzy-dark">
            Congratulations!
          </h2>
          <p className="text-lg text-gray-600">
            Here's your free assembly guide video
          </p>
        </div>

        {/* Email notification - prominent, above the video */}
        <div
          className="bg-goatzy-dark rounded-xl p-6 mb-10 text-center animate-fade-in"
          style={{ animationDelay: '150ms' }}
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <svg className="w-6 h-6 text-goatzy-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-lg md:text-xl text-white font-light">
              Your ebooks have been sent to your email!
            </p>
          </div>
          <p className="text-sm text-goatzy-accent font-light">
            Check your inbox for your download links.
          </p>
        </div>

        {/* Usage Manual PDF */}
        {manualPdfUrl && (
          <div
            className="bg-white rounded-xl border border-goatzy-pale shadow-lg overflow-hidden mb-10 animate-fade-in"
            style={{ animationDelay: '200ms' }}
          >
            {/* Manual Header */}
            <div className="p-6 pb-4 border-b border-goatzy-pale">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-goatzy-pale rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-goatzy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-light tracking-wide text-goatzy-dark">
                      Usage Manual
                    </h3>
                    <p className="text-sm text-gray-500">
                      {displayName} - Instructions
                    </p>
                  </div>
                </div>
                <a
                  href={manualPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center gap-2 px-5 py-2.5 bg-goatzy-dark text-white text-sm font-light tracking-wide rounded-lg
                           hover:bg-goatzy transition-all duration-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </a>
              </div>
            </div>

            {/* PDF Preview */}
            <div className="w-full" style={{ height: '500px' }}>
              <iframe
                src={manualPdfUrl}
                title="Usage Manual"
                className="w-full h-full"
                frameBorder="0"
              />
            </div>
          </div>
        )}

        {/* Video Card */}
        <div
          className="bg-white rounded-xl border border-goatzy-pale shadow-lg overflow-hidden mb-10 animate-fade-in"
          style={{ animationDelay: '300ms' }}
        >
          {/* Video Header */}
          <div className="p-6 pb-4 border-b border-goatzy-pale">
            <div className="flex items-center space-x-3">
              {/* Play icon */}
              <div className="w-10 h-10 bg-goatzy-pale rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-goatzy-dark" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-light tracking-wide text-goatzy-dark">
                  How to Assemble Your {displayName}
                </h3>
                <p className="text-sm text-gray-500">
                  Step-by-step guide by Team Goatzy
                </p>
              </div>
            </div>
          </div>

          {/* YouTube Embed */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0`}
              title={`How to Assemble Your Goatzy ${displayName}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handleVideoPlay}
            />
          </div>

          {/* Video Description */}
          <div className="p-6 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed">
              Follow along with this step-by-step assembly guide to set up your Goatzy {displayName}.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-block px-3 py-1 text-xs font-light bg-goatzy-pale text-goatzy-dark rounded-full">Assembly Guide</span>
              <span className="inline-block px-3 py-1 text-xs font-light bg-goatzy-pale text-goatzy-dark rounded-full">Quick Setup</span>
              <span className="inline-block px-3 py-1 text-xs font-light bg-goatzy-pale text-goatzy-dark rounded-full">All Models</span>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div
          className="bg-white rounded-xl border border-goatzy-pale p-6 mb-10 animate-fade-in"
          style={{ animationDelay: '300ms' }}
        >
          <h3 className="text-lg font-light tracking-wide text-goatzy-dark mb-4">
            Pro Tips from Our Breeders
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-goatzy-pale rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-goatzy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-sm text-gray-700">Assemble on a flat surface for best results</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-goatzy-pale rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-goatzy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-700">Put food in the bowl to keep the goats calm</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-goatzy-pale rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-goatzy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-700">Adjust headpiece height before each use</p>
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        <div
          className="text-center space-y-4 pt-6 border-t border-goatzy-pale animate-fade-in"
          style={{ animationDelay: '450ms' }}
        >
          <p className="text-lg text-goatzy-dark leading-relaxed font-light">
            Thank you for being part of the Goatzy family!
          </p>
          <p className="text-base text-gray-600 leading-relaxed">
            We hope your {displayName} makes farm life easier. If you have any questions, don't hesitate to reach out.
          </p>
          <p className="text-sm text-gray-500 pt-2 italic">
            Farmer tested, built to last.
          </p>
        </div>
      </div>

      {/* Back Button - Bottom */}
      <button
        onClick={onBack}
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2
                   w-12 h-12 rounded-full bg-white border-2 border-goatzy
                   flex items-center justify-center
                   hover:bg-goatzy-bg transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-goatzy-accent focus:ring-offset-2
                   shadow-md"
      >
        <svg className="w-5 h-5 text-goatzy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </div>
  );
};

export default VideoStep;
